import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, Typography, Space, Button, notification, Spin, Empty, Modal, Input, Tag, Divider, Row, Col, Statistic } from 'antd';
import { CrownOutlined, CloseCircleOutlined, ReloadOutlined, HistoryOutlined, LoadingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import CustomNavbar from '../components/Navbar';
import PaymentService from '../services/paymentService';

const { Title, Text } = Typography;
const { TextArea } = Input;

const SubscriptionManagement = () => {
    const [loading, setLoading] = useState(true);
    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [subscriptionHistory, setSubscriptionHistory] = useState([]);
    const [subscriptionStats, setSubscriptionStats] = useState(null);
    const [cancelModalVisible, setCancelModalVisible] = useState(false);
    const [cancellationReason, setCancellationReason] = useState('');
    const [cancelling, setCancelling] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchSubscriptionData();
    }, []);

    const fetchSubscriptionData = async () => {
        setLoading(true);
        try {
            const [currentResponse, historyResponse, statsResponse] = await Promise.all([
                PaymentService.getCurrentSubscription(),
                PaymentService.getSubscriptionHistory(),
                PaymentService.getSubscriptionStats()
            ]);

            if (currentResponse.success) {
                setCurrentSubscription(currentResponse.data);
            }

            if (historyResponse.success) {
                setSubscriptionHistory(historyResponse.data.data);
            }

            if (statsResponse.success) {
                setSubscriptionStats(statsResponse.data);
            }
        } catch (error) {
            console.error('Error fetching subscription data:', error);
            notification.error({
                message: 'Error',
                description: 'Failed to load subscription data. Please try again.',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCancelSubscription = async () => {
        if (!cancellationReason.trim()) {
            notification.error({
                message: 'Cancellation Reason Required',
                description: 'Please provide a reason for cancellation.',
            });
            return;
        }

        setCancelling(true);
        try {
            const response = await PaymentService.cancelSubscription(cancellationReason);

            if (response.success) {
                notification.success({
                    message: 'Subscription Cancelled',
                    description: 'Your subscription has been cancelled successfully.',
                });
                setCancelModalVisible(false);
                setCancellationReason('');
                fetchSubscriptionData(); // Refresh data
            } else {
                notification.error({
                    message: 'Cancellation Failed',
                    description: response.message || 'Failed to cancel subscription.',
                });
            }
        } catch (error) {
            console.error('Error cancelling subscription:', error);
            notification.error({
                message: 'Error',
                description: 'Failed to cancel subscription. Please try again.',
            });
        } finally {
            setCancelling(false);
        }
    };

    const formatCurrency = (amount, currency = 'INR') => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: currency,
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    const getStatusColor = (status) => {
        switch (status.toLowerCase()) {
            case 'active':
                return 'success';
            case 'expired':
                return 'error';
            case 'cancelled':
                return 'default';
            default:
                return 'default';
        }
    };

    const getDaysRemaining = (endDate) => {
        const end = new Date(endDate);
        const now = new Date();
        const diffTime = end - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays > 0 ? diffDays : 0;
    };

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh'
            }}>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: '#AD1457' }} spin />} />
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
            <Helmet><title>My Subscription | Oottan</title></Helmet>
            <CustomNavbar />

            <div style={{ padding: '80px 24px 24px' }}>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    {/* Header */}
                    <Card style={{ borderRadius: '12px' }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div>
                                <Title level={3} style={{ margin: 0 }}>
                                    Subscription Management
                                </Title>
                                <Text type="secondary">
                                    Manage your premium subscription
                                </Text>
                            </div>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={fetchSubscriptionData}
                            >
                                Refresh
                            </Button>
                        </div>
                    </Card>

                    {/* Current Subscription */}
                    <Card style={{ borderRadius: '12px' }}>
                        <Title level={4}>Current Subscription</Title>

                        {currentSubscription ? (
                            <div>
                                <Row gutter={[24, 24]}>
                                    <Col xs={24} md={12}>
                                        <div style={{
                                            background: '#f8f9fa',
                                            padding: '20px',
                                            borderRadius: '8px',
                                            border: '1px solid #e8e8e8'
                                        }}>
                                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <CrownOutlined style={{ color: '#faad14', fontSize: '20px' }} />
                                                    <Text strong style={{ fontSize: '18px' }}>
                                                        {currentSubscription.subscription_plan.name}
                                                    </Text>
                                                </div>

                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Text>Status:</Text>
                                                    <Tag color={getStatusColor(currentSubscription.status)}>
                                                        {currentSubscription.status.toUpperCase()}
                                                    </Tag>
                                                </div>

                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Text>Start Date:</Text>
                                                    <Text>{formatDate(currentSubscription.start_date)}</Text>
                                                </div>

                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Text>End Date:</Text>
                                                    <Text>{formatDate(currentSubscription.end_date)}</Text>
                                                </div>

                                                {currentSubscription.status === 'active' && (
                                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Text>Days Remaining:</Text>
                                                        <Text strong style={{ color: '#52c41a' }}>
                                                            {getDaysRemaining(currentSubscription.end_date)} days
                                                        </Text>
                                                    </div>
                                                )}
                                            </Space>
                                        </div>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <div style={{
                                            background: '#f8f9fa',
                                            padding: '20px',
                                            borderRadius: '8px',
                                            border: '1px solid #e8e8e8'
                                        }}>
                                            <Title level={5}>Plan Details</Title>
                                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Text>Price:</Text>
                                                    <Text strong>
                                                        {formatCurrency(currentSubscription.subscription_plan.price)}
                                                    </Text>
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Text>Duration:</Text>
                                                    <Text>{currentSubscription.subscription_plan.duration_days} days</Text>
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Text>Contact Numbers:</Text>
                                                    <Text>{currentSubscription.subscription_plan.contact_numbers}</Text>
                                                </div>
                                            </Space>

                                            <Divider />

                                            {currentSubscription.status === 'active' && (
                                                <Button
                                                    danger
                                                    icon={<CloseCircleOutlined />}
                                                    onClick={() => setCancelModalVisible(true)}
                                                    style={{ width: '100%' }}
                                                >
                                                    Cancel Subscription
                                                </Button>
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        ) : (
                            <Empty
                                description="No active subscription found"
                                style={{ margin: '40px 0' }}
                            >
                                <Button
                                    type="primary"
                                    onClick={() => navigate('/pricing')}
                                >
                                    View Plans
                                </Button>
                            </Empty>
                        )}
                    </Card>

                    {/* Subscription Statistics */}
                    {subscriptionStats && (
                        <Card style={{ borderRadius: '12px' }}>
                            <Title level={4}>Subscription Statistics</Title>
                            <Row gutter={[24, 24]}>
                                <Col xs={12} sm={6}>
                                    <Statistic
                                        title="Total Subscriptions"
                                        value={subscriptionStats.total_subscriptions}
                                        valueStyle={{ color: '#1890ff' }}
                                    />
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Statistic
                                        title="Active Subscriptions"
                                        value={subscriptionStats.active_subscriptions}
                                        valueStyle={{ color: '#52c41a' }}
                                    />
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Statistic
                                        title="Total Spent"
                                        value={subscriptionStats.total_spent}
                                        precision={2}
                                        prefix="₹"
                                        valueStyle={{ color: '#faad14' }}
                                    />
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Statistic
                                        title="Expired Subscriptions"
                                        value={subscriptionStats.expired_subscriptions}
                                        valueStyle={{ color: '#ff4d4f' }}
                                    />
                                </Col>
                            </Row>
                        </Card>
                    )}

                    {/* Subscription History */}
                    <Card style={{ borderRadius: '12px' }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '16px'
                        }}>
                            <Title level={4} style={{ margin: 0 }}>
                                Subscription History
                            </Title>
                            <Button
                                icon={<HistoryOutlined />}
                                onClick={() => navigate('/payment-history')}
                            >
                                View All Payments
                            </Button>
                        </div>

                        {subscriptionHistory.length > 0 ? (
                            <div>
                                {subscriptionHistory.slice(0, 5).map((subscription, index) => (
                                    <div
                                        key={subscription.id}
                                        style={{
                                            padding: '16px',
                                            border: '1px solid #e8e8e8',
                                            borderRadius: '8px',
                                            marginBottom: '12px',
                                            background: index === 0 ? '#f6ffed' : 'white'
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <Text strong>{subscription.subscription_plan.name}</Text>
                                                <br />
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    {formatDate(subscription.start_date)} - {formatDate(subscription.end_date)}
                                                </Text>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <Tag color={getStatusColor(subscription.status)}>
                                                    {subscription.status.toUpperCase()}
                                                </Tag>
                                                <br />
                                                <Text strong style={{ fontSize: '12px' }}>
                                                    {formatCurrency(subscription.payment.amount)}
                                                </Text>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {subscriptionHistory.length > 5 && (
                                    <div style={{ textAlign: 'center', marginTop: '16px' }}>
                                        <Button onClick={() => navigate('/payment-history')}>
                                            View All History
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <Empty
                                description="No subscription history found"
                                style={{ margin: '20px 0' }}
                            />
                        )}
                    </Card>
                </Space>
            </div>

            {/* Cancel Subscription Modal */}
            <Modal
                title="Cancel Subscription"
                open={cancelModalVisible}
                onOk={handleCancelSubscription}
                onCancel={() => {
                    setCancelModalVisible(false);
                    setCancellationReason('');
                }}
                confirmLoading={cancelling}
                okText="Cancel Subscription"
                cancelText="Keep Subscription"
                okButtonProps={{ danger: true }}
            >
                <div style={{ marginBottom: '16px' }}>
                    <Text>Are you sure you want to cancel your subscription?</Text>
                </div>
                <div style={{ marginBottom: '16px' }}>
                    <Text strong>Please provide a reason for cancellation:</Text>
                </div>
                <TextArea
                    rows={4}
                    value={cancellationReason}
                    onChange={(e) => setCancellationReason(e.target.value)}
                    placeholder="Please tell us why you're cancelling..."
                />
                <div style={{ marginTop: '16px' }}>
                    <Text type="secondary">
                        Note: Your subscription will remain active until the end of the current billing period.
                    </Text>
                </div>
            </Modal>
        </div>
    );
};

export default SubscriptionManagement;
